import classnames from 'classnames';
import { isEditOrPreviewMode } from '@/lib/editModeHelpers';
import {
  POECTabsContainerBlock as POECTabsContainerBlockProps,
  POECTabItemBlock as POECTabItemBlockProps
} from '@/@types/content';
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import Button from '@/components/ui/Buttons/Button';
import GlobalTabs, { TabTrigger } from '../../ui/Tab';
import Panel from '@/components/ui/Panel';
import Text from '@/components/cms/Text';
import ContentArea from '@/components/cms/ContentArea';
import { ContentReference, IContent } from '@/@types/cms';
import Block from '@/components/cms/Block';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { transformIfPLIUrl } from '@/utils/helpers';
import { useGetContentQuery } from '@/redux/api/contentDeliveryAPI';
import Shimmer from '@/components/ui/Shimmer';

type TabBlockTab = TabTrigger & {
  content?: IContent[];
  contentLink?: ContentReference;
};

export type POECTabItemProps = POECTabItemBlockProps;

const POECTabItemBlock: React.FC<
  POECTabsContainerBlockProps & {
    lazyLoadTabContents?: boolean;
  }
> = ({ tabs, overlayHeading, overlayButtonText, overlayContent, lazyLoadTabContents, url }) => {
  const [isOpen, setIsOpen] = useState(false);
  const tabsList = tabs as POECTabItemBlockProps[];
  const editMode = isEditOrPreviewMode();
  const [searchParams] = useSearchParams();
  const activetab = searchParams.get('activetab');
  const [tabsBlocks, setTabsBlocks] = useState<TabBlockTab[]>(() => {
    return (
      tabsList?.map(tab => ({
        label: tab.tabTitle ?? '',
        id: tab.tabTitle || '',

        contentLink: tab.contentLink,
        content: tab.content
      })) || []
    );
  });
  const [isPendingTransition, startTransition] = useTransition();

  const navigate = useNavigate();
  const parentRouteSegment = transformIfPLIUrl(url)?.substring(1);

  const selectedTabBlock = useMemo(
    () =>
      tabsBlocks.find(tab => tab.id.toUpperCase() === activetab?.toUpperCase()) ?? tabsBlocks[0],
    [activetab, tabsBlocks]
  );

  const handleTabChange = useCallback(
    (tab: TabBlockTab) => {
      navigate(`/${parentRouteSegment}?activetab=${tab.id}`);
    },
    [navigate, parentRouteSegment]
  );

  const onChange = useCallback((value: boolean) => {
    setIsOpen(value);
  }, []);

  const {
    data: tabContent,
    isError,
    isFetching,
    isLoading
  } = useGetContentQuery(
    {
      contentLink: selectedTabBlock?.contentLink?.id?.toString() ?? '',
      query: {
        select: ['content']
      }
    },
    { skip: false }
  );

  const isLoadingTabContents = useMemo(
    () => !!isLoading || !!isFetching || !!isPendingTransition,
    [isFetching, isLoading, isPendingTransition]
  );

  useEffect(() => {
    if (!isLoading && !isFetching && !isError && !!tabContent?.content) {
      startTransition(() => {
        setTabsBlocks(prev =>
          prev.map(a => ({
            ...a,
            content:
              a.contentLink?.id == selectedTabBlock?.contentLink?.id
                ? (tabContent?.content as IContent[])
                : a.content
          }))
        );
      });
    }
  }, [tabContent, isError, isFetching, isLoading, selectedTabBlock?.contentLink?.id]);

  const modalElement = (
    <Panel
      dataComponent="POECTabsContainerBlock-Panel"
      isOpen={isOpen}
      onOpenChange={() => onChange(!isOpen)}
    >
      <Panel.Trigger asChild>
        {(overlayButtonText || editMode) && (
          <Button
            propertyName="OverlayButtonText"
            color="black"
            size="large"
            label={overlayButtonText}
            className="w-full"
          />
        )}
      </Panel.Trigger>
      <Panel.Content>
        <Panel.Header className="!h-7">
          <Text className="heading-5">{overlayHeading}</Text>
        </Panel.Header>
        <Panel.Body>
          <ContentArea
            propertyName="ProductSubBlock"
            components={overlayContent}
            className={classnames('grid grid-rows-1 gap-4')}
          />
        </Panel.Body>
      </Panel.Content>
    </Panel>
  );

  const loadingShimmer = useMemo(() => {
    return (
      <div className="container">
        <Shimmer className="mb-5 mt-20 h-10 w-56" />
        <Shimmer className="mb-20 mt-5 h-96 w-full" />
        {Array.from({ length: 4 }).map((_, j) => {
          return (
            <div key={`collection-${j}`}>
              <Shimmer className="mb-5 mt-20 h-10 w-56" />
              <div className="mb-16 flex w-full flex-row flex-wrap justify-between">
                {Array.from({ length: 4 }).map((_, i) => {
                  return (
                    <Shimmer
                      key={`shimmer-${i}`}
                      className="mb-10 mt-2 h-[375px] w-full lg:w-[362px]"
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, []);

  return (
    <Block className="min-h-screen">
      {
        <GlobalTabs
          tabsTriggers={tabsBlocks}
          variant="page"
          selectedValue={selectedTabBlock}
          setSelectedValue={handleTabChange}
          registerButton={modalElement}
          container={true}
          sticky={true}
        >
          {isLoadingTabContents
            ? loadingShimmer
            : !!selectedTabBlock?.content && (
                <ContentArea
                  className="mt-10"
                  propertyName="Item"
                  components={selectedTabBlock.content}
                />
              )}
        </GlobalTabs>
      }
    </Block>
  );
};

export default POECTabItemBlock;
